/**
 * Frontend Error Handling for Guest Management System
 * Provides comprehensive error handling with user-friendly messages and retry logic
 */

export enum GuestManagementErrorType {
  // Authentication and Authorization
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  
  // Booking Related Errors
  BOOKING_NOT_FOUND = 'BOOKING_NOT_FOUND',
  BOOKING_ACCESS_DENIED = 'BOOKING_ACCESS_DENIED',
  BOOKING_ALREADY_COMPLETE = 'BOOKING_ALREADY_COMPLETE',
  INVALID_BOOKING_STATUS = 'INVALID_BOOKING_STATUS',
  
  // Guest Account Errors
  GUEST_ACCOUNT_EXISTS = 'GUEST_ACCOUNT_EXISTS',
  GUEST_ACCOUNT_NOT_FOUND = 'GUEST_ACCOUNT_NOT_FOUND',
  GUEST_ACCOUNT_CREATION_FAILED = 'GUEST_ACCOUNT_CREATION_FAILED',
  INVALID_GUEST_CONTACT = 'INVALID_GUEST_CONTACT',
  
  // Check-in Form Errors
  FORM_URL_EXPIRED = 'FORM_URL_EXPIRED',
  FORM_URL_INVALID = 'FORM_URL_INVALID',
  FORM_ALREADY_SUBMITTED = 'FORM_ALREADY_SUBMITTED',
  FORM_VALIDATION_FAILED = 'FORM_VALIDATION_FAILED',
  DUPLICATE_SUBMISSION = 'DUPLICATE_SUBMISSION',
  
  // Document Management Errors
  DOCUMENT_UPLOAD_FAILED = 'DOCUMENT_UPLOAD_FAILED',
  DOCUMENT_NOT_FOUND = 'DOCUMENT_NOT_FOUND',
  DOCUMENT_ACCESS_DENIED = 'DOCUMENT_ACCESS_DENIED',
  INVALID_DOCUMENT_FORMAT = 'INVALID_DOCUMENT_FORMAT',
  DOCUMENT_SIZE_EXCEEDED = 'DOCUMENT_SIZE_EXCEEDED',
  
  // Communication Errors
  EMAIL_DELIVERY_FAILED = 'EMAIL_DELIVERY_FAILED',
  WHATSAPP_DELIVERY_FAILED = 'WHATSAPP_DELIVERY_FAILED',
  COMMUNICATION_SERVICE_UNAVAILABLE = 'COMMUNICATION_SERVICE_UNAVAILABLE',
  INVALID_COMMUNICATION_TEMPLATE = 'INVALID_COMMUNICATION_TEMPLATE',
  
  // Rate Limiting and Abuse Prevention
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  TOO_MANY_FORM_SUBMISSIONS = 'TOO_MANY_FORM_SUBMISSIONS',
  SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
  IP_BLOCKED = 'IP_BLOCKED',
  
  // Data Validation Errors
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',
  INVALID_EMAIL_FORMAT = 'INVALID_EMAIL_FORMAT',
  INVALID_PHONE_FORMAT = 'INVALID_PHONE_FORMAT',
  INVALID_NAME_FORMAT = 'INVALID_NAME_FORMAT',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
  
  // General System Errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface GuestManagementError {
  type: GuestManagementErrorType;
  message: string;
  details?: string;
  statusCode?: number;
  retryable: boolean;
  retryAfter?: number;
  suggestedAction?: string;
  userMessage?: string;
  context?: Record<string, any>;
}

export interface ErrorDisplayInfo {
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
  actionText?: string;
  onAction?: () => void;
  retryable: boolean;
  icon?: string;
  details?: string;
}

/**
 * Frontend error handler for guest management operations
 */
export class GuestManagementErrorHandler {
  
  /**
   * Parse API error response into standardized error format
   */
  static parseApiError(error: any): GuestManagementError {
    // Handle axios/fetch errors
    if (error.response) {
      const { status, data } = error.response;
      
      // Check if backend sent structured error
      if (data?.error?.code) {
        return {
          type: data.error.code as GuestManagementErrorType,
          message: data.error.message,
          details: data.error.details,
          statusCode: status,
          retryable: data.error.retryable || false,
          retryAfter: data.error.retryAfter,
          suggestedAction: data.error.suggestedAction,
          userMessage: data.error.message,
          context: data.error.context
        };
      }
      
      // Handle standard HTTP errors
      return this.createErrorFromStatus(status, data?.message || error.message);
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: GuestManagementErrorType.NETWORK_ERROR,
        message: 'Request timed out',
        userMessage: 'The request took too long to complete. Please try again.',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Check your internet connection and try again'
      };
    }
    
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return {
        type: GuestManagementErrorType.NETWORK_ERROR,
        message: 'Network connection failed',
        userMessage: 'Unable to connect to the server. Please check your internet connection.',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Check your internet connection and try again'
      };
    }
    
    // Default unknown error
    return {
      type: GuestManagementErrorType.UNKNOWN_ERROR,
      message: error.message || 'An unexpected error occurred',
      userMessage: 'Something went wrong. Please try again.',
      retryable: true,
      retryAfter: 60,
      suggestedAction: 'Please try again or contact support if the problem persists'
    };
  }
  
  /**
   * Create error from HTTP status code
   */
  private static createErrorFromStatus(status: number, message?: string): GuestManagementError {
    switch (status) {
      case 400:
        return {
          type: GuestManagementErrorType.FORM_VALIDATION_FAILED,
          message: message || 'Invalid request',
          userMessage: 'Please check your input and try again.',
          retryable: false,
          suggestedAction: 'Verify all required fields are filled correctly'
        };
      
      case 401:
        return {
          type: GuestManagementErrorType.AUTHENTICATION_REQUIRED,
          message: 'Authentication required',
          userMessage: 'Please log in to continue.',
          retryable: false,
          suggestedAction: 'Log in to your account'
        };
      
      case 403:
        return {
          type: GuestManagementErrorType.INSUFFICIENT_PERMISSIONS,
          message: 'Access denied',
          userMessage: 'You do not have permission to perform this action.',
          retryable: false,
          suggestedAction: 'Contact your administrator for access'
        };
      
      case 404:
        return {
          type: GuestManagementErrorType.BOOKING_NOT_FOUND,
          message: 'Resource not found',
          userMessage: 'The requested resource could not be found.',
          retryable: false,
          suggestedAction: 'Please verify the information and try again'
        };
      
      case 409:
        return {
          type: GuestManagementErrorType.FORM_ALREADY_SUBMITTED,
          message: 'Conflict',
          userMessage: 'This action has already been completed.',
          retryable: false,
          suggestedAction: 'Refresh the page to see the current status'
        };
      
      case 410:
        return {
          type: GuestManagementErrorType.FORM_URL_EXPIRED,
          message: 'Resource expired',
          userMessage: 'This link has expired.',
          retryable: false,
          suggestedAction: 'Please request a new link from your host'
        };
      
      case 413:
        return {
          type: GuestManagementErrorType.DOCUMENT_SIZE_EXCEEDED,
          message: 'File too large',
          userMessage: 'The file you uploaded is too large.',
          retryable: false,
          suggestedAction: 'Please upload a smaller file (max 10MB)'
        };
      
      case 429:
        return {
          type: GuestManagementErrorType.RATE_LIMIT_EXCEEDED,
          message: 'Too many requests',
          userMessage: 'You are making requests too quickly.',
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please wait a few minutes before trying again'
        };
      
      case 500:
        return {
          type: GuestManagementErrorType.INTERNAL_SERVER_ERROR,
          message: 'Server error',
          userMessage: 'Something went wrong on our end.',
          retryable: true,
          retryAfter: 60,
          suggestedAction: 'Please try again in a moment'
        };
      
      case 503:
        return {
          type: GuestManagementErrorType.SERVICE_UNAVAILABLE,
          message: 'Service temporarily unavailable',
          userMessage: 'The service is temporarily unavailable.',
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please try again in a few minutes'
        };
      
      default:
        return {
          type: GuestManagementErrorType.UNKNOWN_ERROR,
          message: message || `HTTP ${status} error`,
          userMessage: 'An unexpected error occurred.',
          retryable: status >= 500,
          retryAfter: 60,
          suggestedAction: 'Please try again or contact support'
        };
    }
  }
  
  /**
   * Format error for user display
   */
  static formatForDisplay(error: GuestManagementError, onRetry?: () => void): ErrorDisplayInfo {
    const errorMap: Partial<Record<GuestManagementErrorType, Partial<ErrorDisplayInfo>>> = {
      [GuestManagementErrorType.AUTHENTICATION_REQUIRED]: {
        title: 'Authentication Required',
        severity: 'warning',
        icon: '🔐'
      },
      [GuestManagementErrorType.INSUFFICIENT_PERMISSIONS]: {
        title: 'Access Denied',
        severity: 'error',
        icon: '🚫'
      },
      [GuestManagementErrorType.BOOKING_NOT_FOUND]: {
        title: 'Booking Not Found',
        severity: 'error',
        icon: '🏠'
      },
      [GuestManagementErrorType.FORM_URL_EXPIRED]: {
        title: 'Link Expired',
        severity: 'warning',
        icon: '⏰'
      },
      [GuestManagementErrorType.FORM_ALREADY_SUBMITTED]: {
        title: 'Already Completed',
        severity: 'info',
        icon: '✅'
      },
      [GuestManagementErrorType.FORM_VALIDATION_FAILED]: {
        title: 'Validation Error',
        severity: 'error',
        icon: '⚠️'
      },
      [GuestManagementErrorType.DOCUMENT_UPLOAD_FAILED]: {
        title: 'Upload Failed',
        severity: 'error',
        icon: '📄'
      },
      [GuestManagementErrorType.DOCUMENT_SIZE_EXCEEDED]: {
        title: 'File Too Large',
        severity: 'error',
        icon: '📁'
      },
      [GuestManagementErrorType.INVALID_DOCUMENT_FORMAT]: {
        title: 'Invalid File Format',
        severity: 'error',
        icon: '📄'
      },
      [GuestManagementErrorType.EMAIL_DELIVERY_FAILED]: {
        title: 'Email Delivery Failed',
        severity: 'warning',
        icon: '📧'
      },
      [GuestManagementErrorType.WHATSAPP_DELIVERY_FAILED]: {
        title: 'WhatsApp Delivery Failed',
        severity: 'warning',
        icon: '💬'
      },
      [GuestManagementErrorType.RATE_LIMIT_EXCEEDED]: {
        title: 'Too Many Requests',
        severity: 'warning',
        icon: '⏱️'
      },
      [GuestManagementErrorType.NETWORK_ERROR]: {
        title: 'Connection Error',
        severity: 'warning',
        icon: '🌐'
      },
      [GuestManagementErrorType.SERVICE_UNAVAILABLE]: {
        title: 'Service Unavailable',
        severity: 'warning',
        icon: '🔧'
      },
      [GuestManagementErrorType.INTERNAL_SERVER_ERROR]: {
        title: 'Server Error',
        severity: 'error',
        icon: '⚙️'
      },
      [GuestManagementErrorType.UNKNOWN_ERROR]: {
        title: 'Unexpected Error',
        severity: 'error',
        icon: '❌'
      }
    };
    
    const errorInfo = errorMap[error.type] || {};
    
    let message = error.userMessage || error.message;
    if (error.suggestedAction) {
      message += `\n\n${error.suggestedAction}`;
    }
    
    let actionText: string | undefined;
    let actionHandler: (() => void) | undefined;
    
    if (error.retryable && onRetry) {
      if (error.retryAfter) {
        const minutes = Math.ceil(error.retryAfter / 60);
        actionText = `Retry in ${minutes}m`;
        // Set up delayed retry
        setTimeout(() => {
          actionText = 'Retry Now';
          actionHandler = onRetry;
        }, error.retryAfter * 1000);
      } else {
        actionText = 'Try Again';
        actionHandler = onRetry;
      }
    }
    
    return {
      title: errorInfo.title || 'Error',
      message,
      severity: errorInfo.severity || 'error',
      actionText,
      onAction: actionHandler,
      retryable: error.retryable,
      icon: errorInfo.icon,
      details: error.details
    };
  }
  
  /**
   * Get user-friendly error message for common scenarios
   */
  static getQuickMessage(error: GuestManagementError): string {
    const quickMessages: Partial<Record<GuestManagementErrorType, string>> = {
      [GuestManagementErrorType.AUTHENTICATION_REQUIRED]: 'Please log in to continue',
      [GuestManagementErrorType.INSUFFICIENT_PERMISSIONS]: 'You do not have permission for this action',
      [GuestManagementErrorType.BOOKING_NOT_FOUND]: 'Booking not found',
      [GuestManagementErrorType.FORM_URL_EXPIRED]: 'This check-in link has expired',
      [GuestManagementErrorType.FORM_ALREADY_SUBMITTED]: 'Check-in form already completed',
      [GuestManagementErrorType.DOCUMENT_UPLOAD_FAILED]: 'Failed to upload document',
      [GuestManagementErrorType.DOCUMENT_SIZE_EXCEEDED]: 'File size too large (max 10MB)',
      [GuestManagementErrorType.INVALID_DOCUMENT_FORMAT]: 'Please upload a valid image file',
      [GuestManagementErrorType.EMAIL_DELIVERY_FAILED]: 'Failed to send email',
      [GuestManagementErrorType.WHATSAPP_DELIVERY_FAILED]: 'Failed to send WhatsApp message',
      [GuestManagementErrorType.RATE_LIMIT_EXCEEDED]: 'Too many requests - please wait',
      [GuestManagementErrorType.NETWORK_ERROR]: 'Connection error - check your internet',
      [GuestManagementErrorType.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable',
      [GuestManagementErrorType.INTERNAL_SERVER_ERROR]: 'Server error - please try again',
      [GuestManagementErrorType.UNKNOWN_ERROR]: 'Something went wrong'
    };
    
    return quickMessages[error.type] || error.userMessage || error.message;
  }
  
  /**
   * Determine if error should trigger automatic retry
   */
  static shouldAutoRetry(error: GuestManagementError, attemptCount: number = 0): boolean {
    if (!error.retryable || attemptCount >= 3) {
      return false;
    }
    
    // Don't auto-retry user errors
    const nonAutoRetryTypes = [
      GuestManagementErrorType.AUTHENTICATION_REQUIRED,
      GuestManagementErrorType.INSUFFICIENT_PERMISSIONS,
      GuestManagementErrorType.FORM_VALIDATION_FAILED,
      GuestManagementErrorType.FORM_ALREADY_SUBMITTED,
      GuestManagementErrorType.FORM_URL_EXPIRED,
      GuestManagementErrorType.INVALID_DOCUMENT_FORMAT,
      GuestManagementErrorType.DOCUMENT_SIZE_EXCEEDED,
      GuestManagementErrorType.RATE_LIMIT_EXCEEDED
    ];
    
    return !nonAutoRetryTypes.includes(error.type);
  }
  
  /**
   * Calculate retry delay with exponential backoff
   */
  static getRetryDelay(error: GuestManagementError, attemptCount: number = 0): number {
    if (error.retryAfter) {
      return error.retryAfter * 1000; // Convert to milliseconds
    }
    
    // Exponential backoff: 2s, 4s, 8s, 16s, etc.
    const baseDelay = 2000;
    return Math.min(baseDelay * Math.pow(2, attemptCount), 30000); // Max 30 seconds
  }
  
  /**
   * Log error for debugging
   */
  static logError(error: GuestManagementError, context?: Record<string, any>): void {
    const logData = {
      type: error.type,
      message: error.message,
      statusCode: error.statusCode,
      retryable: error.retryable,
      context: { ...error.context, ...context }
    };
    
    if (error.statusCode && error.statusCode >= 500) {
      console.error('Guest management error (server):', logData);
    } else if (error.statusCode && error.statusCode >= 400) {
      console.warn('Guest management error (client):', logData);
    } else {
      console.info('Guest management info:', logData);
    }
  }
}

/**
 * React hook for handling guest management errors
 */
export function useGuestManagementErrorHandler() {
  const handleError = (error: any, context?: Record<string, any>) => {
    const guestError = GuestManagementErrorHandler.parseApiError(error);
    GuestManagementErrorHandler.logError(guestError, context);
    return guestError;
  };
  
  const formatError = (error: GuestManagementError, onRetry?: () => void) => {
    return GuestManagementErrorHandler.formatForDisplay(error, onRetry);
  };
  
  const shouldRetry = (error: GuestManagementError, attemptCount: number = 0) => {
    return GuestManagementErrorHandler.shouldAutoRetry(error, attemptCount);
  };
  
  const getRetryDelay = (error: GuestManagementError, attemptCount: number = 0) => {
    return GuestManagementErrorHandler.getRetryDelay(error, attemptCount);
  };
  
  const getQuickMessage = (error: GuestManagementError) => {
    return GuestManagementErrorHandler.getQuickMessage(error);
  };
  
  return {
    handleError,
    formatError,
    shouldRetry,
    getRetryDelay,
    getQuickMessage
  };
}